import { getSecTransactionCodeInfo } from "./sec-transaction-codes";
import type {
  CanonicalTransaction,
  SignalBucket
} from "./types";

export interface TransactionDescription {
  activityLabel: string;
  signalLabel: string;
  codeMeaning: string;
  badgeVariant: "buy" | "sell" | "warning" | "muted";
}

const SIGNAL_LABELS: Record<SignalBucket, string> = {
  informative_buy: "Informative buy",
  informative_sell: "Informative sell",
  uninformative_buy: "Uninformative buy",
  uninformative_sell: "Uninformative sell",
  needs_review: "Needs review"
};

function badgeForSignal(bucket: SignalBucket): TransactionDescription["badgeVariant"] {
  if (bucket === "informative_buy") return "buy";
  if (bucket === "informative_sell") return "sell";
  if (bucket === "needs_review") return "muted";
  return "warning";
}

export function describeTransaction(
  transaction: CanonicalTransaction
): TransactionDescription {
  const codeInfo = getSecTransactionCodeInfo(transaction.transactionCode);
  const bucket = transaction.signal.bucket;
  const planStatus = transaction.plan.status;
  const signalLabel = SIGNAL_LABELS[bucket];
  const badgeVariant = badgeForSignal(bucket);

  if (transaction.tradeType === "sell" && planStatus === "planned") {
    return {
      activityLabel: "Planned sale",
      signalLabel,
      codeMeaning: codeInfo?.codeMeaning ?? "Planned or routine sale",
      badgeVariant
    };
  }

  if (codeInfo) {
    return {
      activityLabel: codeInfo.activityLabel,
      signalLabel,
      codeMeaning: codeInfo.codeMeaning,
      badgeVariant
    };
  }

  const activityLabel =
    transaction.tradeType === "award"
      ? "Stock award"
      : transaction.tradeType === "option"
        ? "Option exercise"
        : transaction.tradeType === "sell"
          ? "Sale"
          : "Administrative filing";
  const codeMeaning =
    transaction.tradeType === "award"
      ? "Award or compensation transaction"
      : transaction.tradeType === "option"
        ? "Option, derivative, or conversion transaction"
        : transaction.tradeType === "sell"
          ? "Disposition reported on Form 4"
          : "Unmapped SEC transaction code";
  return { activityLabel, signalLabel, codeMeaning, badgeVariant };
}
